"use client";

import React from "react";

import "./styles.css";

import { ImageLinkSkeleton as HeroLinkSkeleton } from "@/atoms/links/ImageLink/skeleton";
import { ImageLink } from "@/atoms/links";
import { useProfile } from "@/domains/profile/queries";
import { buildSocialUrl } from "@/lib/social-urls";
import { SectionErrorBoundary } from "@/shared/ErrorBoundary";

interface HeroContentProps {
  name?: string;
  size: number;
  sizes?: string;
  className: string;
  imageSrc?: string;
}

const HeroContent = ({
  name,
  size,
  sizes,
  className,
  imageSrc,
}: HeroContentProps): React.JSX.Element => {
  const {
    data: profile,
    isLoading: isLoadingProfile,
    isError: isErrorProfile,
  } = useProfile(1); // Pass ID

  // When imageSrc is provided (e.g. about page), use it; otherwise fallback to profile
  const resolvedSrc = imageSrc ?? profile?.avatar ?? "/images/profile/hero.png";
  const resolvedAlt = name || profile?.nickname || "Hero";
  const profileLinkedIn =
    profile && "linkedIn" in profile
      ? (profile as { linkedIn?: string }).linkedIn
      : profile?.linkedin;
  const resolvedHref = profileLinkedIn || buildSocialUrl("linkedin") || "#";

  if (isLoadingProfile && !imageSrc) {
    return (
      <HeroLinkSkeleton className={`hero-image ${className}`} size={size} />
    );
  }

  if (!imageSrc && (isErrorProfile || !profile)) {
    return <div>Error loading profile</div>;
  }

  // Add fade-in animation class when image loads
  return (
    <ImageLink
      href={resolvedHref}
      src={resolvedSrc}
      alt={resolvedAlt}
      size={size}
      sizes={sizes}
      className={`${className} hero-image--loaded`}
      target="_blank"
      rel="noopener noreferrer"
    />
  );
};

const Hero = (props: HeroContentProps): React.JSX.Element => {
  return (
    <SectionErrorBoundary sectionName="Profile">
      <HeroContent {...props} />
    </SectionErrorBoundary>
  );
};

export default Hero;
